// src/init.ts
import type { LoggerOptions, LogContext } from "./core/types.js";
import { Logger } from "./core/logger.js";
import { getRuntimeContext, getEnvVar } from "./runtime/index.js";
import {
  DEFAULT_OPTIONS,
  ENV,
  LOG_LEVELS,
  type LogLevel,
} from "./core/constants.js";
import type { ErrorHandler } from "./core/errors.js";
import { ConfigurationError } from "./core/errors.js";

// Add debug logging
const DEBUG = process.env.NODE_ENV === "development.js";
function debugLog(...args: any[]) {
  if (DEBUG) {
    console.log("[INIT_DEBUG]", ...args);
  }
}

interface InitOptions {
  serviceName?: string;
  reporters?: LoggerOptions["reporters"];
  errorHandler?: ErrorHandler;
  context?: LogContext;
  formatOptions?: LoggerOptions["formatOptions"];
  level?: number;
}

let loggerInstance: Logger | null = null;

function validateOptions(options: InitOptions) {
  if (options.level !== undefined && (options.level < 0 || options.level > 5)) {
    throw new ConfigurationError("Log level must be between 0 and 5");
  }
}

// Safe stringify that handles circular references
function safeStringify(obj: any): string {
  try {
    const seen = new WeakSet();
    return JSON.stringify(
      obj,
      (key, value) => {
        // Handle circular references
        if (typeof value === "object" && value !== null) {
          if (seen.has(value)) {
            return "[Circular].js";
          }
          seen.add(value);
        }

        // Handle functions
        if (typeof value === "function") {
          return "[Function].js";
        }

        return value;
      },
      2
    );
  } catch (err: any) {
    return `[Cannot stringify: ${err.message}]`;
  }
}

/**
 * Initialize the logger synchronously with basic configuration
 */
export function initLogger(options: InitOptions = {}): Logger {
  debugLog("initLogger called with options:", safeStringify(options));
  validateOptions(options);

  const runtimeContext = getRuntimeContext();
  debugLog("Runtime context:", safeStringify(runtimeContext));

  // Improve context handling to support direct serviceName
  if (
    options.serviceName &&
    (!options.context || !options.context.serviceName)
  ) {
    options.context = {
      ...(options.context || {}),
      serviceName: options.serviceName,
    };
    debugLog("Added serviceName to context:", safeStringify(options.context));
  }

  // Create default context with runtime information
  const defaultContext: LogContext = {
    environment: runtimeContext.environment as any,
    runtime: runtimeContext.runtime,
    serviceName: "default-service",
  };
  debugLog("Default context:", safeStringify(defaultContext));

  const mergedOptions: LoggerOptions = {
    ...DEFAULT_OPTIONS,
    ...options,
    context: {
      ...defaultContext,
      ...options.context,
    },
    formatOptions: {
      ...DEFAULT_OPTIONS.formatOptions,
      ...options.formatOptions,
    },
  };
  debugLog("Merged options:", safeStringify(mergedOptions));

  // Ensure reporters are properly set
  if (options.reporters) {
    debugLog("Setting reporters from options:", typeof options.reporters);
    mergedOptions.reporters = options.reporters;
  }
  debugLog("Final reporters count:", mergedOptions.reporters?.length);

  // Create new logger instance with error handler
  debugLog("Creating logger instance");
  loggerInstance = Logger.getInstance(mergedOptions);

  if (!loggerInstance) {
    throw new Error("Failed to create logger instance");
  }

  // Set log level
  const level = options.level ?? detectLogLevel();
  debugLog("Setting log level:", level);
  loggerInstance.setLevel(level);

  debugLog("Logger instance created successfully");
  return loggerInstance;
}

/**
 * Get the current logger instance or create a new one with default options
 */
export function getLogger(): Logger {
  debugLog("getLogger called, loggerInstance exists:", !!loggerInstance);
  if (!loggerInstance) {
    return initLogger();
  }
  return loggerInstance;
}

/**
 * Create a new logger with additional context
 */
export function createLogger(context: LogContext): Logger {
  debugLog("createLogger called with context:", safeStringify(context));
  return getLogger().withContext(context);
}

// Helper function to detect appropriate log level
function detectLogLevel(): number {
  const envLogLevel = getEnvVar("LOG_LEVEL") as LogLevel | undefined;
  if (envLogLevel && envLogLevel in LOG_LEVELS) {
    return LOG_LEVELS[envLogLevel];
  }

  const runtimeContext = getRuntimeContext();
  switch (runtimeContext.environment) {
    case ENV.production:
      return LOG_LEVELS.INFO;
    case ENV.test:
      return LOG_LEVELS.ERROR;
    case ENV.development:
    default:
      return LOG_LEVELS.DEBUG;
  }
}
